import { useEffect, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import hoodieAsset from "@/assets/totally-card-1-hoodie-v3.webp.asset.json";
import sweaterAsset from "@/assets/totally-card-2-sweater-v3.webp.asset.json";
import beanieAsset from "@/assets/totally-card-3-beanie-v3.webp.asset.json";
import bannerAsset from "@/assets/med-section-v2.webp.asset.json";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Shirt, Palette, Truck } from "@/components/Icon";

export interface SolidColor {
  kind: "solid";
  name: string;
  hex: string;
}

export interface TwoToneColor {
  kind: "two-tone";
  name: string;
  colors: [string, string];
}

export type TotallyProductColor = SolidColor | TwoToneColor;

export interface TotallyProduct {
  key: "hoodie" | "sweater" | "beanie";
  name: string;
  model?: string;
  price: string;
  priceNote?: string;
  description: string;
  /** Meerdere afbeeldingen mogelijk; bij 1 afbeelding tonen we geen gallery. */
  images: { src: string; objectPosition: string; alt: string }[];
  colors: TotallyProductColor[];
  colorPdf: string;
}

function luminance(hex: string): number {
  const rgb = hex
    .replace("#", "")
    .match(/.{2}/g)
    ?.map((s) => parseInt(s, 16) / 255) ?? [0, 0, 0];
  const [r, g, b] = rgb.map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function isLightColor(color: TotallyProductColor): boolean {
  const hexes = color.kind === "solid" ? [color.hex] : color.colors;
  return hexes.some((hex) => luminance(hex) > 0.7);
}

/** Gedeelde swatch-styling: gebruikt door de productcards én de formulier-selects. */
export function colorSwatchStyle(color: TotallyProductColor): React.CSSProperties {
  const background =
    color.kind === "solid"
      ? { backgroundColor: color.hex }
      : {
          background: `linear-gradient(90deg, ${color.colors[0]} 0%, ${color.colors[0]} 50%, ${color.colors[1]} 50%, ${color.colors[1]} 100%)`,
        };
  return {
    ...background,
    ...(isLightColor(color) ? { border: "1px solid rgba(0,0,0,0.12)" } : {}),
  };
}

/** Presentational kleurbolletje (geen interactie). */
export function ColorDot({
  color,
  className = "w-4 h-4",
}: {
  color: TotallyProductColor;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block rounded-full shrink-0 ${className}`}
      style={colorSwatchStyle(color)}
    />
  );
}

function ColorSwatch({
  color,
  active,
  onActivate,
  onPreview,
  onPreviewEnd,
  hidden,
  innerRef,
}: {
  color: TotallyProductColor;
  active: boolean;
  onActivate: () => void;
  onPreview: () => void;
  onPreviewEnd: () => void;
  hidden?: boolean;
  innerRef?: (el: HTMLButtonElement | null) => void;
}) {
  return (
    <button
      type="button"
      ref={innerRef}
      title={color.name}
      aria-label={color.name}
      aria-pressed={active}
      onClick={onActivate}
      onMouseEnter={onPreview}
      onMouseLeave={onPreviewEnd}
      onFocus={onPreview}
      onBlur={onPreviewEnd}
      className={`w-5 h-5 rounded-full shrink-0 p-0 transition-[outline-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        active ? "outline outline-2 outline-offset-2 outline-primary" : ""
      } ${hidden ? "hidden" : ""}`}
      style={colorSwatchStyle(color)}
    />
  );
}


function ProductColors({ colors }: { colors: TotallyProductColor[] }) {
  const isMobile = useIsMobile();
  const [activeName, setActiveName] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const shownName = previewName ?? activeName;

  const collapsing = isMobile && !expanded && visibleCount !== null;
  const measuring = isMobile && visibleCount === null;

  useEffect(() => {
    if (!isMobile) {
      setVisibleCount(null);
      return;
    }
    if (visibleCount !== null) return;
    const els = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    if (els.length === 0) return;
    const firstTop = els[0].offsetTop;
    const rowTops: number[] = [];
    for (const el of els) {
      if (!rowTops.includes(el.offsetTop)) rowTops.push(el.offsetTop);
    }
    const limitTop = rowTops[1] ?? firstTop;
    let count = 0;
    for (const el of els) {
      if (el.offsetTop <= limitTop) count++;
    }
    setVisibleCount(count);
  }, [isMobile, visibleCount, colors.length]);

  useEffect(() => {
    const onResize = () => setVisibleCount(null);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const hiddenCount = collapsing ? Math.max(0, colors.length - (visibleCount ?? 0)) : 0;

  return (
    <div className="mt-4">
      <div className="text-base font-medium text-muted-foreground mb-2">
        {shownName ? (
          <>
            <span className="text-[#0a2933] font-bold">Productkleur</span>
            <span className="text-[#0a2933]"> - {shownName}</span>
          </>
        ) : (
          <span className="text-[#0a2933] font-bold">Productkleuren</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {colors.map((c, i) => (
          <ColorSwatch
            key={c.name}
            color={c}
            active={activeName === c.name}
            hidden={!measuring && collapsing && i >= (visibleCount ?? 0)}
            innerRef={(el) => {
              itemRefs.current[i] = el;
            }}
            onActivate={() =>
              setActiveName((current) => (current === c.name ? null : c.name))
            }
            onPreview={() => setPreviewName(c.name)}
            onPreviewEnd={() => setPreviewName(null)}
          />
        ))}
        {isMobile && (hiddenCount > 0 || expanded) && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-medium text-primary underline underline-offset-2"
          >
            {expanded ? "Minder tonen" : `+ ${hiddenCount} kleuren`}
          </button>
        )}
      </div>
    </div>
  );
}


export const TOTALLY_PRODUCTS: TotallyProduct[] = [
  {
    key: "hoodie",
    name: "Urban hoodie",
    model: "R1067",
    price: "vanaf € 21,00",
    priceNote: "incl. btw en bedrukking",
    description:
      "Stevige, warme hoodie met zachte binnenkant. Perfect voor op de piste én in de après-ski.",
    images: [
      {
        src: hoodieAsset.url,
        objectPosition: "50% 50%",
        alt: "Vrouw draagt zwarte Urban hoodie met bedrukking in de bergen",
      },
    ],
    colors: [
      { kind: "solid", name: "White", hex: "#FFFFFF" },
      { kind: "solid", name: "Yellow", hex: "#F7E83C" },
      { kind: "solid", name: "Red", hex: "#E33530" },
      { kind: "solid", name: "Sand", hex: "#F2C794" },
      { kind: "solid", name: "Orange", hex: "#FE8529" },
      { kind: "solid", name: "Lilac", hex: "#C2A5F8" },
      { kind: "solid", name: "Purple", hex: "#653564" },
      { kind: "solid", name: "Light pink", hex: "#FFB9C2" },
      { kind: "solid", name: "Turquoise", hex: "#32D2C6" },
      { kind: "solid", name: "Royal blue", hex: "#344E8C" },
      { kind: "solid", name: "Bottle green", hex: "#5D8453" },
      { kind: "solid", name: "Heather grey", hex: "#BDBCBA" },
      { kind: "solid", name: "Black", hex: "#000000" },
      { kind: "solid", name: "Angora", hex: "#D9B9AA" },
      { kind: "solid", name: "Navy Blue", hex: "#031D42" },
      { kind: "solid", name: "Zen Blue", hex: "#A2A9BD" },
      { kind: "solid", name: "Vintage White", hex: "#F0E6E5" },
      { kind: "solid", name: "Corail", hex: "#FD756E" },
      { kind: "solid", name: "Ebony", hex: "#383F46" },
      { kind: "solid", name: "Garnet", hex: "#8C221A" },
      { kind: "solid", name: "Rossette", hex: "#DC276B" },
      { kind: "solid", name: "Venture Green", hex: "#535F4A" },
      { kind: "solid", name: "Mist Green", hex: "#D4DCC5" },
      { kind: "solid", name: "Storm blue", hex: "#5B6F84" },
      { kind: "solid", name: "Berry Red", hex: "#875760" },
      { kind: "solid", name: "Pale Red", hex: "#A5787E" },
      { kind: "solid", name: "Curry Yellow", hex: "#BB7D39" },
      { kind: "solid", name: "Ash white melange", hex: "#EEEEEE" },
      { kind: "solid", name: "Laurel green", hex: "#A9BA9F" },
      { kind: "solid", name: "Mocha", hex: "#C09B98" },
      { kind: "solid", name: "Stone Grey", hex: "#8B8C7B" },
      { kind: "solid", name: "Irish purple", hex: "#5C5B9B" },
      { kind: "solid", name: "Blue lake", hex: "#3A4E5B" },
      { kind: "two-tone", name: "Heather grey / Black", colors: ["#BDBCBA", "#000000"] },
      { kind: "two-tone", name: "Red / Black", colors: ["#E33530", "#000000"] },
      { kind: "two-tone", name: "Bottle green / Marl Grey", colors: ["#5D8453", "#808080"] },
      { kind: "two-tone", name: "Sky blue / White", colors: ["#8CACD1", "#FFFFFF"] },
      { kind: "two-tone", name: "Kelly Green / White", colors: ["#008D52", "#FFFFFF"] },
      { kind: "two-tone", name: "Royal blue / White", colors: ["#344E8C", "#FFFFFF"] },
      { kind: "two-tone", name: "Light pink / Marl Grey", colors: ["#FFB9C2", "#808080"] },
      { kind: "two-tone", name: "Navy Blue / Marl Grey", colors: ["#031D42", "#808080"] },
      { kind: "two-tone", name: "White / Navy Blue", colors: ["#FFFFFF", "#031D42"] },
      { kind: "two-tone", name: "Black / Marl Grey", colors: ["#000000", "#808080"] },
      { kind: "two-tone", name: "Maroon / Marl Grey", colors: ["#6B2233", "#808080"] },
    ],
    colorPdf: "",
  },
  {
    key: "sweater",
    name: "Clasica sweater",
    model: "R1070",
    price: "vanaf € 18,00",
    priceNote: "incl. btw en bedrukking",
    description:
      "Klassieke crewneck sweater met een comfortabele pasvorm. Tijdloos en ideaal om te bedrukken.",
    images: [
      {
        src: sweaterAsset.url,
        objectPosition: "50% 50%",
        alt: "Man draagt beige Clasica sweater met bedrukking in de bergen",
      },
    ],
    colors: [
      { kind: "solid", name: "White", hex: "#FFFFFF" },
      { kind: "solid", name: "Yellow", hex: "#F7E83C" },
      { kind: "solid", name: "Red", hex: "#E33530" },
      { kind: "solid", name: "Sand", hex: "#F2C794" },
      { kind: "solid", name: "Orange", hex: "#FE8529" },
      { kind: "solid", name: "Purple", hex: "#653564" },
      { kind: "solid", name: "Light pink", hex: "#FFB9C2" },
      { kind: "solid", name: "Turquoise", hex: "#32D2C6" },
      { kind: "solid", name: "Ocean blue", hex: "#168DC0" },
      { kind: "solid", name: "Sky blue", hex: "#8CACD1" },
      { kind: "solid", name: "Royal blue", hex: "#344E8C" },
      { kind: "solid", name: "Bottle green", hex: "#5D8453" },
      { kind: "solid", name: "Black", hex: "#000000" },
      { kind: "solid", name: "Angora", hex: "#D9B9AA" },
      { kind: "solid", name: "Navy Blue", hex: "#031D42" },
      { kind: "solid", name: "Zen Blue", hex: "#A2A9BD" },
      { kind: "solid", name: "Vintage White", hex: "#F0E6E5" },
      { kind: "solid", name: "Corail", hex: "#FD756E" },
      { kind: "solid", name: "Ebony", hex: "#383F46" },
      { kind: "solid", name: "Garnet", hex: "#8C221A" },
      { kind: "solid", name: "Marl Grey", hex: "#808080" },
      { kind: "solid", name: "Dark Lead", hex: "#484E44" },
      { kind: "solid", name: "Rossette", hex: "#DC276B" },
      { kind: "solid", name: "Venture Green", hex: "#535F4A" },
      { kind: "solid", name: "Kelly Green", hex: "#008D52" },
      { kind: "solid", name: "Mist Green", hex: "#D4DCC5" },
      { kind: "solid", name: "Oasis Green", hex: "#C0D78A" },
      { kind: "solid", name: "Storm blue", hex: "#5B6F84" },
      { kind: "solid", name: "Berry Red", hex: "#875760" },
      { kind: "solid", name: "Pale Red", hex: "#A5787E" },
      { kind: "solid", name: "Curry Yellow", hex: "#BB7D39" },
      { kind: "solid", name: "Ash white melange", hex: "#EEEEEE" },
      { kind: "solid", name: "Laurel green", hex: "#A9BA9F" },
      { kind: "solid", name: "Lilac", hex: "#6A6276" },
      { kind: "solid", name: "Mocha", hex: "#C09B98" },
      { kind: "solid", name: "Stone Grey", hex: "#8B8C7B" },
      { kind: "solid", name: "Irish purple", hex: "#5C5B9B" },
      { kind: "solid", name: "Blue lake", hex: "#3A4E5B" },
    ],
    colorPdf: "",
  },
  {
    key: "beanie",
    name: "Beanie",
    price: "vanaf € 8,50",
    priceNote: "incl. btw en bedrukking",
    description:
      "Gebreide muts die altijd past. Compleet maken met jullie logo in borduur- of drukwerk.",
    images: [
      {
        src: beanieAsset.url,
        objectPosition: "50% 50%",
        alt: "Vrouw met zwarte beanie en roze hoodie met rugbedrukking",
      },
    ],
    colors: [
      { kind: "solid", name: "White", hex: "#FFFFFF" },
      { kind: "solid", name: "Black", hex: "#222220" },
      { kind: "solid", name: "Almond", hex: "#EDE0D8" },
      { kind: "solid", name: "Bottle Green", hex: "#00302A" },
      { kind: "solid", name: "Bright Royal", hex: "#0E2F77" },
      { kind: "solid", name: "Burgundy", hex: "#700F2D" },
      { kind: "solid", name: "Classic Red", hex: "#CA233C" },
      { kind: "solid", name: "Dusty Pink", hex: "#EDCBCE" },
      { kind: "solid", name: "French Navy", hex: "#181D45" },
      { kind: "solid", name: "Fuchsia", hex: "#DE2877" },
      { kind: "solid", name: "Graphite Grey", hex: "#737371" },
      { kind: "solid", name: "Heather Grey", hex: "#D9DDE0" },
      { kind: "solid", name: "Kelly Green", hex: "#006525" },
      { kind: "solid", name: "Olive Green", hex: "#463929" },
      { kind: "solid", name: "Orange", hex: "#F37133" },
      { kind: "solid", name: "Purple", hex: "#5E3D71" },
    ],
    colorPdf: "",
  },
];

const USPS: { label: string; Icon: typeof Check }[] = [
  { label: "Eigen ontwerp", Icon: Shirt },
  { label: "Bedrukking inbegrepen", Icon: Palette },
  { label: "Levering ca. 2–3 weken", Icon: Truck },
];

interface Props {
  onSelectProduct: (key: TotallyProduct["key"]) => void;
}

const TotallyAanbod = ({ onSelectProduct }: Props) => (
  <section id="aanbod" className="relative scroll-mt-32 py-10 md:py-14">
    <div
      className="absolute inset-y-0 left-1/2 -translate-x-1/2 bg-[#fcfcfc] w-screen"
      aria-hidden
    />

    <div className="relative">
      <div className="relative overflow-hidden rounded-lg border border-border/70 bg-white">
        {/* Fotografie */}
        <img
          src={bannerAsset.url}
          alt="Drie studenten met bedrukte hoodies, sweater en beanie op wintersport"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-[65%_center] md:object-[70%_center]"
        />
        {/* Leesbaarheidsverloop links, vloeit weg naar het midden */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-white via-white/85 to-white/10 md:bg-gradient-to-r md:from-white md:via-white/85 md:to-transparent md:w-[70%]"
          aria-hidden
        />
        <div className="relative grid md:grid-cols-[minmax(0,40%)_1fr]">
          <div className="p-6 sm:p-8 md:p-10 lg:p-12 pb-40 sm:pb-52 md:pb-12 md:flex md:flex-col md:justify-center">
            <p className="text-xs md:text-sm font-semibold tracking-[0.14em] uppercase text-primary">
              Totally snow merchandise
            </p>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold text-[#0a2933] leading-tight">
              Maak jullie skitrip compleet
            </h2>
            <p className="mt-4 text-muted-foreground text-base max-w-md">
              Kies jullie favoriete item, bepaal de kleur en laat 'm bedrukken met het logo of
              ontwerp van jullie groep.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
              {USPS.map(({ label, Icon }) => (
                <li
                  key={label}
                  className="flex items-center gap-3 text-sm font-medium text-[#0a2933]"
                >
                  <Icon className="w-5 h-5 md:w-[22px] md:h-[22px] text-primary shrink-0" />
                  {label}
                </li>
              ))}
            </ul>
          </div>
          <div className="hidden md:block min-h-[460px] lg:min-h-[540px]" aria-hidden />
        </div>
      </div>

      <div className="mt-10 mb-6 max-w-2xl">
        <h3 className="text-2xl md:text-3xl font-bold text-[#0a2933] mb-2">Kies jullie favoriet</h3>
        <p className="text-muted-foreground">
          Hoodie, sweater of beanie? Bekijk de opties en kies wat bij jullie groep past.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {TOTALLY_PRODUCTS.map((p) => {
          const img = p.images[0];
          return (
            <article
              key={p.key}
              className="flex flex-col bg-white border border-border/70 rounded-lg overflow-hidden"
            >
              <div className="w-full aspect-[4/5] overflow-hidden bg-muted">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: img.objectPosition }}
                />
              </div>

              <div className="flex flex-col flex-1 p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <h4 className="text-lg font-bold text-[#0a2933]">{p.name}</h4>
                  <span className="text-base font-semibold text-primary whitespace-nowrap">
                    {p.price}
                  </span>
                </div>
                <div className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                  {p.model ? `Model ${p.model}` : "\u00a0"}
                </div>

                <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>

                <p className="mt-2 flex items-center gap-2 text-sm font-medium text-[#0a2933]">
                  <Check className="w-4 h-4 text-primary" />
                  Inclusief bedrukking
                </p>
                {p.priceNote && (
                  <p className="mt-1 text-xs text-muted-foreground">{p.priceNote}</p>
                )}

                <ProductColors colors={p.colors} />

                <div className="mt-auto pt-5">
                  <Button className="w-full" onClick={() => onSelectProduct(p.key)}>
                    Kies dit product <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

export default TotallyAanbod;
